import { useRef, useState } from "react";
import { ArrowLeft, ArrowRight, Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, imgSrc, errMsg } from "@/lib/api";

export async function uploadImage(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.src;
}

// Upload, reorder and remove a list of image paths. First image is the main one.
export default function ImageListEditor({ images, onChange, max = 12, testid = "images" }) {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);

  const onFiles = async (files) => {
    setBusy(true);
    const added = [];
    for (const f of Array.from(files).slice(0, max - images.length)) {
      try { added.push(await uploadImage(f)); } catch (e) { toast.error(`${f.name}: ${errMsg(e)}`); }
    }
    onChange([...images, ...added]);
    setBusy(false);
    if (input.current) input.current.value = "";
  };
  const move = (i, d) => {
    const next = [...images];
    [next[i], next[i + d]] = [next[i + d], next[i]];
    onChange(next);
  };

  return (
    <div data-testid={testid}>
      <div className="flex flex-wrap gap-3">
        {images.map((src, i) => (
          <div key={src + i} data-testid={`${testid}-item-${i}`} className="group relative h-32 w-24 overflow-hidden bg-[#333336]">
            <img src={imgSrc(src)} alt="" className="h-full w-full object-cover object-top" />
            {i === 0 && <span className="absolute left-1 top-1 bg-paper/90 px-1.5 py-0.5 font-mono text-[0.5rem] uppercase tracking-widest">Main</span>}
            <div className="absolute inset-x-0 bottom-0 flex justify-between bg-ink/70 p-1 transition-opacity md:opacity-0 md:group-hover:opacity-100">
              <button type="button" data-testid={`${testid}-left-${i}`} disabled={i === 0} onClick={() => move(i, -1)} className="text-paper disabled:opacity-30" aria-label="Move left"><ArrowLeft className="h-3.5 w-3.5" /></button>
              <button type="button" data-testid={`${testid}-remove-${i}`} onClick={() => onChange(images.filter((_, j) => j !== i))} className="text-paper" aria-label="Remove"><X className="h-3.5 w-3.5" /></button>
              <button type="button" data-testid={`${testid}-right-${i}`} disabled={i === images.length - 1} onClick={() => move(i, 1)} className="text-paper disabled:opacity-30" aria-label="Move right"><ArrowRight className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        ))}
        {images.length < max && (
          <button type="button" data-testid={`${testid}-upload-btn`} onClick={() => input.current?.click()} disabled={busy}
            className="flex h-32 w-24 flex-col items-center justify-center gap-2 border border-dashed border-ink/30 text-stone transition-colors hover:border-ink hover:text-ink">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" strokeWidth={1.4} />}
            <span className="font-body text-[0.65rem]">{busy ? "Uploading" : "Add photos"}</span>
          </button>
        )}
      </div>
      <input ref={input} type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple hidden data-testid={`${testid}-file-input`} onChange={(e) => onFiles(e.target.files)} />
      <p className="mt-2 font-body text-xs text-muted">Hover a photo to reorder or remove it. The first photo is the main one.</p>
    </div>
  );
}
